"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inquiries = [{
  question: "How does Inclusive Voices News ensure unbiased reporting?",
  answer: "We adhere to a rigorous editorial process that prioritizes multiple perspectives, fact-checking, and transparency. Our journalists are committed to the highest standards of journalistic integrity, ensuring that every story is vetted for accuracy and fairness before publication."
}, {
  question: "How can I submit a story or tip to your newsroom?",
  answer: "We welcome grassroots stories and community tips. You can submit your information through our secure tip line on the Contact page. Every submission is reviewed by our editorial team to determine its alignment with our mission of amplifying underrepresented voices."
}, {
  question: "Is Inclusive Voices News a non-profit organization?",
  answer: "Yes, we operate as a mission-driven platform dedicated to public service journalism. Our funding comes from community support, grants, and partnerships that align with our commitment to diversity, inclusion, and unbiased truth."
}, {
  question: "How do you select the voices and stories you feature?",
  answer: "Our selection process is guided by our commitment to amplifying those who are often overlooked by mainstream media. We actively seek out stories that challenge the status quo, highlight community resilience, and provide a platform for diverse cultural and social perspectives."
}, {
  question: "Can I participate in community discussions or comment on articles?",
  answer: "Absolutely. We encourage constructive dialogue within our community. All comments are moderated to ensure a safe, respectful, and inclusive environment for all readers. We believe that healthy debate is essential to building a stronger, more informed society."
}, {
  question: "Do you offer opportunities for freelance contributors?",
  answer: "Yes, we are always looking for passionate writers, multimedia creators, and journalists who share our vision. Please visit our Connect page to learn more about our submission guidelines and how to join our network of contributors."
}, {
  question: "How can I support the mission of Inclusive Voices News?",
  answer: "You can support us by subscribing to our newsletter, sharing our content, participating in our community events, or becoming a sustaining member. Your support directly enables us to continue our work in delivering independent, inclusive news."
}, {
  question: "Who owns the content published on your platform?",
  answer: "Inclusive Voices News retains the rights to the editorial content produced by our staff. For freelance contributions, we work under specific agreements that respect the creator's rights while allowing us to share their important stories with our global audience."
}];
export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const toggle = (i: number) => {
    setOpenIndex(prev => prev === i ? null : i);
  };
  return <section data-section-id="10590" id="frequently-asked" className="bg-background text-foreground border-b border-border py-24 md:py-32 lg:py-44">
      <div className="mx-auto w-full max-w-[1440px] px-5 md:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-6">
          {}
          <div className="lg:col-span-4 lg:col-start-2 mb-12 lg:mb-0">
            <div ref={leftRef} className={["lg:sticky lg:top-32 transition-all duration-500 ease-out", leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"].join(" ")} style={{
            transitionTimingFunction: "cubic-bezier(0.2, 0.6, 0.2, 1)"
          }}>
              <h2 className="font-default font-bold text-foreground leading-[1.02] tracking-tighter" style={{
              fontSize: "clamp(2.25rem, 4.2vw, 4rem)"
            }}>
                Frequently Asked
              </h2>
              <p className="mt-8 font-default font-normal text-foreground leading-[1.5]" style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.25rem)"
            }}>
                Answers to the most common questions about our mission, editorial standards, and how you can engage with our community. If you need further assistance, our team is here to help.
              </p>

              <div className="mt-12">
                <Link to="/ConnectPage" className="group relative inline-flex items-center font-default font-medium text-foreground" style={{
                fontSize: "0.9375rem"
              }}>
                  <span aria-hidden="true" className="absolute left-0 top-1/2 h-px w-0 -translate-y-1/2 bg-[var(--primary)] transition-[width] duration-[250ms] ease-out group-hover:w-4" />
                  <span className="transition-[padding] duration-[250ms] ease-out group-hover:pl-6">
                    Contact our team →
                  </span>
                </Link>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={["lg:col-span-7 lg:col-start-6 transition-all duration-500 ease-out", rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"].join(" ")} style={{
          transitionTimingFunction: "cubic-bezier(0.2, 0.6, 0.2, 1)"
        }}>
            <div className="border-t border-border">
              {inquiries.map((item, i) => {
              const isOpen = openIndex === i;
              return <div key={i} data-index={i} className="border-b border-border">
                    <button type="button" onClick={() => toggle(i)} aria-expanded={isOpen} className="group flex w-full items-start justify-between gap-6 py-8 text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]">
                      <span className="font-default font-semibold text-foreground leading-[1.3] tracking-tight max-w-[80%]" style={{
                    fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)"
                  }}>
                        {item.question}
                      </span>
                      <span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center mt-1">
                        <Plus className={["h-5 w-5 text-foreground transition-transform duration-[250ms] ease-out", isOpen ? "rotate-45" : "rotate-0"].join(" ")} strokeWidth={1.5} />
                      </span>
                    </button>
                    <div className="grid overflow-hidden transition-[grid-template-rows] duration-[250ms] ease-out" style={{
                  gridTemplateRows: isOpen ? "1fr" : "0fr"
                }}>
                      <div className="overflow-hidden">
                        <div className="pt-0 pb-8 pr-0 lg:pr-12">
                          <p className="font-default font-normal text-foreground leading-[1.5] max-w-[80ch]" style={{
                        fontSize: "clamp(1.0625rem, 1.15vw, 1.25rem)"
                      }}>
                            {item.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}